import { Conversation, ChatMessage } from '../schemas/chat';
import { ConversationView, FileMetadata, Turn, TurnStatus } from '../types/conversation';

/**
 * 将 ChatMessage 转换为 Turn
 * 从消息对（user + assistant）转换为 Turn 结构
 */
function messagesToTurns(messages: ChatMessage[]): Turn[] {
  const turns: Turn[] = [];

  // Sort messages by created_at to ensure correct order
  // If created_at is the same, ensure user comes before assistant
  const sortedMessages = [...messages].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;

    if (aTime !== bTime) {
      return aTime - bTime;
    }

    // If timestamps are equal, ensure user comes before assistant
    // This handles the case where API returns messages out of order
    if (a.role === 'user' && b.role === 'assistant') {
      return -1;
    }
    if (a.role === 'assistant' && b.role === 'user') {
      return 1;
    }

    return 0;
  });

  // 将消息对转换为 Turn
  for (let i = 0; i < sortedMessages.length; i += 2) {
    const userMessage = sortedMessages[i];
    const assistantMessage = sortedMessages[i + 1] || null;

    if (userMessage.role !== 'user') {
      // 跳过非用户消息（可能是系统消息）
      continue;
    }

    // 确定 Turn 状态
    let status: TurnStatus = 'completed';
    let answerText = '';
    let thinking = '';
    let error: { message: string; code?: string } | undefined = undefined;

    if (assistantMessage) {
      if (assistantMessage.role === 'assistant') {
        answerText = assistantMessage.content || '';
        thinking = assistantMessage.thinking_process?.content || '';

        if (assistantMessage.error_data) {
          status = 'failed';
          error = {
            message:
              typeof assistantMessage.error_data === 'string'
                ? assistantMessage.error_data
                : assistantMessage.error_data?.message || 'Unknown error',
            code: assistantMessage.error_data?.code,
          };
        } else if (!(assistantMessage.is_done ?? true)) {
          // Message is not done yet - it's actively streaming
          status = 'streaming';
        } else {
          status = 'completed';
        }
      }
    } else {
      // 只有用户消息，没有助手回复，可能是刚创建
      status = 'created';
    }

    // 获取 parent_id（用于版本关系）
    const parentTurnId = userMessage.parent_id || undefined;

    // 获取创建时间
    const createdAt = userMessage.created_at
      ? new Date(userMessage.created_at).getTime()
      : Date.now();

    const turn: Turn = {
      id: `turn-${userMessage.id}`,
      parentTurnId,
      content: {
        text: userMessage.content || '',
        attachments: Array.isArray(userMessage.files)
          ? userMessage.files.map(f => {
              // Handle different file object structures
              if (typeof f === 'string') {
                // If it's already just an ID string, create minimal metadata
                return { file_id: f, file_name: 'Unknown', file_type: 'document' };
              }
              // Extract metadata from file object
              // The backend may return files in different formats:
              // 1. {file_id, file_name, file_type} - from message responses
              // 2. {id, original_name, file_type} - from file upload API
              // 3. Other variations
              const fileId = (f as any).file_id || (f as any).id || '';
              const fileName =
                (f as any).file_name || (f as any).name || (f as any).original_name || 'Unknown';
              const fileType = (f as any).file_type || 'document';

              return {
                file_id: fileId,
                file_name: fileName,
                file_type: fileType,
              } as FileMetadata;
            })
          : [],
      },
      status,
      answerText,
      thinking,
      error,
      modelRef: assistantMessage?.model_name || assistantMessage?.model_id || undefined,
      createdAt,
    };

    turns.push(turn);
  }

  return turns;
}

/**
 * 将 Conversation 转换为 ConversationView（基于新的 Turn 抽象）
 */
export function toConversationView(conversation: Conversation): ConversationView {
  const messages = conversation.messages || [];
  const turns = messagesToTurns(messages);

  return {
    id: conversation.id,
    title: conversation.title,
    turns,
    currentTurn: turns.length > 0 ? turns[turns.length - 1] : null,
    model: conversation.model_id || undefined,
    knowledgeBaseIds: conversation.knowledge_base_ids || undefined,
    folderId: conversation.folder_id || undefined,
    literatureSources: conversation.literature_sources,
    literatureOptions: conversation.literature_options || undefined,
    archived: conversation.is_archived,
    pinned: conversation.is_pinned,
    createdAt: conversation.created_at,
    updatedAt: conversation.updated_at,
  };
}

/**
 * 从 ConversationView 获取所有消息（用于向后兼容）
 * 将 Turn 转换回 ChatMessage 格式
 */
export function getMessagesFromView(view: ConversationView): ChatMessage[] {
  const messages: ChatMessage[] = [];

  for (const turn of view.turns) {
    // 用户消息
    const userMessage: ChatMessage = {
      id: `user-${turn.id}`,
      role: 'user',
      content: turn.content.text,
      files: turn.content.attachments?.map(a => a.file_id) || [],
      created_at: new Date(turn.createdAt).toISOString(),
      parent_id: turn.parentTurnId || null,
      is_done: true,
    };
    messages.push(userMessage);

    // 助手消息（如果有）
    if (turn.answerText || turn.status === 'streaming') {
      const assistantMessage: ChatMessage = {
        id: `assistant-${turn.id}`,
        role: 'assistant',
        content: turn.answerText,
        created_at: new Date(turn.createdAt).toISOString(),
        is_done: turn.status === 'completed',
        model_id: turn.modelRef || null,
        error_data: turn.error || null,
      };
      messages.push(assistantMessage);
    }
  }

  return messages;
}
